import {useNavigate} from 'react-router-dom';
import {trpc, type RouterOutput} from '@common-web/lib/trpc';
import {Alert, AlertDescription, AlertTitle} from '@common-web/components/ui/alert';
import {Button} from '@common-web/components/ui/button';
import {useDataTable} from '@common-web/components/data-table';
import {Skeleton} from '@common-web/components/ui/skeleton';
import {
  RefreshCw,
  ChevronRight,
  ChevronLeft,
  AlertCircle,
  Loader2,
  TableProperties,
  Activity,
} from 'lucide-react';
import {formatDistanceToNow, format} from 'date-fns';

type SyncRun = RouterOutput['internal']['vbaSyncCas']['list']['runs'][number];

/**
 * A run's status is about one thing: did the sync finish? It is deliberately NOT tinted by the error
 * count. Almost every full run carries errors, and those errors are the sync doing its job — refusing
 * to write a change it couldn't prove safe. Colouring a completed run amber for that would paint the
 * whole page as alarming when nothing is wrong, and would blur the one state that IS alarming.
 * Error and warning counts are reported alongside, on their own, where they can be read on their own.
 */
type Health = 'completed' | 'failed' | 'pending';

function healthOf(run: SyncRun): Health {
  if (run.incomplete) return 'pending';
  return run.status === 'FAILED' ? 'failed' : 'completed';
}

const HEALTH: Record<Health, {dot: string; bar: string; text: string; label: string}> = {
  completed: {dot: 'bg-emerald', bar: 'bg-emerald', text: 'text-emerald', label: 'Completed'},
  failed: {dot: 'bg-red-500', bar: 'bg-red-500', text: 'text-red-500', label: 'Failed'},
  pending: {dot: 'bg-slate-300', bar: 'bg-slate-200', text: 'text-slate-400', label: 'Processing'},
};

const num = (n: number) => n.toLocaleString();

/** A labelled figure. Numbers are tabular so columns of them line up as you scan down the page. */
function Figure({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: string;
  tone?: 'default' | 'muted' | 'error' | 'warning';
}) {
  const toneClass =
    tone === 'error'
      ? 'text-red-500'
      : tone === 'warning'
        ? 'text-amber'
        : tone === 'muted'
          ? 'text-slate-400'
          : 'text-brand-navy';
  return (
    <div>
      <div className={`text-xl font-semibold tabular-nums tracking-tight ${toneClass}`}>
        {value}
      </div>
      <div className='text-[11px] uppercase tracking-wider text-slate-400 mt-0.5'>{label}</div>
    </div>
  );
}

/**
 * The recent run strip: one labelled tile per run, oldest to newest.
 *
 * An earlier version drew these as bars whose height encoded row volume. That was a mistake — volume
 * barely varies between nights, so the bars looked arbitrary while implying a quantity worth reading.
 * Every tile is now the same size and says which day it is; the only thing encoded is whether the run
 * finished, which is the thing you actually scan a week of runs for.
 */
function RunStrip({runs, onSelect}: {runs: SyncRun[]; onSelect: (id: string) => void}) {
  if (runs.length === 0) return null;

  return (
    <div className='flex items-stretch gap-2 overflow-x-auto pb-1'>
      {[...runs].reverse().map((run) => {
        const h = HEALTH[healthOf(run)];
        const started = new Date(run.startedAt);
        return (
          <button
            key={run.id}
            onClick={() => onSelect(run.id)}
            title={`${format(started, 'EEEE, MMM d')} at ${format(started, 'h:mm a')} — ${num(run.groups)} groups, ${num(run.errorCount)} errors`}
            className='flex-1 min-w-[68px] rounded-xl border border-slate-200 bg-white px-2.5 py-2.5 text-center hover:border-brand-blue/40 hover:bg-slate-50 transition-colors'
          >
            <div className='text-[11px] uppercase tracking-wider text-slate-400'>
              {format(started, 'EEE')}
            </div>
            <div className='text-sm font-medium text-brand-navy tabular-nums'>
              {format(started, 'd MMM')}
            </div>
            <div className={`mt-2 h-1.5 rounded-full ${h.bar}`} />
          </button>
        );
      })}
    </div>
  );
}

/** The newest run, given the prominence it deserves — it answers "did last night work?". */
function LatestRun({run, onOpen}: {run: SyncRun; onOpen: () => void}) {
  const h = HEALTH[healthOf(run)];
  const started = new Date(run.startedAt);

  return (
    <button
      onClick={onOpen}
      className='w-full text-left rounded-2xl border border-slate-200 bg-white p-6 hover:border-brand-blue/40 hover:shadow-lg hover:shadow-brand-blue/5 transition-all group'
    >
      <div className='flex items-start justify-between gap-6 flex-wrap'>
        <div className='min-w-0'>
          <div className='flex items-center gap-2.5'>
            <span className={`h-2.5 w-2.5 rounded-full ${h.dot}`} />
            <span className='text-[11px] uppercase tracking-wider text-slate-400'>Latest run</span>
          </div>
          <div className='mt-2 text-2xl font-semibold text-brand-navy tracking-tight'>
            {format(started, 'EEEE, MMM d')}
          </div>
          <div className='text-sm text-slate-500 mt-0.5'>
            {format(started, 'h:mm a')} · {formatDistanceToNow(started, {addSuffix: true})}
          </div>
        </div>

        <div className='flex items-center gap-8 flex-wrap'>
          {/*
            Counts carry their own colour, while the run's STATUS stays green — the two are different
            questions. Zero reads muted grey rather than red, so a clean figure looks clean.
          */}
          <Figure label='Groups' value={run.incomplete ? '—' : num(run.groups)} />
          <Figure label='Member rows' value={run.incomplete ? '—' : num(run.memberRows)} />
          <Figure
            label='Errors'
            value={run.incomplete ? '—' : num(run.errorCount)}
            tone={run.errorCount > 0 ? 'error' : 'muted'}
          />
          <Figure
            label='Warnings'
            value={run.incomplete ? '—' : num(run.warningCount)}
            tone={run.warningCount > 0 ? 'warning' : 'muted'}
          />
          <ChevronRight className='h-5 w-5 text-slate-300 group-hover:text-brand-blue transition-colors' />
        </div>
      </div>
    </button>
  );
}

/** One row of the run feed. */
function RunRow({run, onOpen}: {run: SyncRun; onOpen: () => void}) {
  const h = HEALTH[healthOf(run)];
  const started = new Date(run.startedAt);

  return (
    <button
      onClick={onOpen}
      className='w-full text-left px-5 py-4 flex items-center gap-5 hover:bg-slate-50/80 transition-colors group'
    >
      {/* Date block */}
      <div className='w-24 shrink-0'>
        <div className='text-sm font-medium text-brand-navy'>{format(started, 'MMM d')}</div>
        <div className='text-xs text-slate-400'>{format(started, 'h:mm a')}</div>
      </div>

      {/* Health */}
      <div className='w-32 shrink-0 flex items-center gap-2'>
        <span className={`h-2 w-2 rounded-full shrink-0 ${h.dot}`} />
        <span className={`text-sm ${h.text}`}>{h.label}</span>
      </div>

      {/* Counts */}
      <div className='flex-1 min-w-0 flex items-center gap-6 text-sm tabular-nums'>
        {run.incomplete ? (
          <span className='flex items-center gap-2 text-slate-400'>
            <Loader2 className='h-3.5 w-3.5 animate-spin' />
            Summarizing…
          </span>
        ) : (
          <>
            <span className='text-slate-600'>
              <span className='font-medium text-brand-navy'>{num(run.groups)}</span> groups
            </span>
            <span className='text-slate-600 hidden sm:inline'>
              <span className='font-medium text-brand-navy'>{num(run.memberRows)}</span> member rows
            </span>
            {run.errorCount > 0 && (
              <span className='text-red-500 font-medium'>{num(run.errorCount)} errors</span>
            )}
            {run.warningCount > 0 && (
              <span className='text-amber font-medium hidden md:inline'>
                {num(run.warningCount)} warnings
              </span>
            )}
          </>
        )}
      </div>

      <ChevronRight className='h-4 w-4 text-slate-300 group-hover:text-brand-blue transition-colors shrink-0' />
    </button>
  );
}

export function VbaSyncCasRunsList() {
  const navigate = useNavigate();
  const table = useDataTable({defaultPageSize: 20});

  const {
    data: runsData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = trpc.internal.vbaSyncCas.list.useQuery({page: table.page, pageSize: table.pageSize});

  const {
    data: stats,
    isLoading: statsLoading,
    error: statsError,
    refetch: refetchStats,
  } = trpc.internal.vbaSyncCas.getStats.useQuery({days: 7});

  // Whole numbers show plain (100%), fractional values show 2 decimals (99.74%).
  const formatRate = (rate?: number) => {
    const v = rate ?? 0;
    return Number.isInteger(v) ? `${v}%` : `${v.toFixed(2)}%`;
  };

  // A failed stats query must not render as zeros — "0 errors" and "couldn't load" mean very
  // different things when you're checking whether last night's sync was clean.
  const stat = (render: () => string) => (statsError ? '—' : render());

  // The aggregates cover only runs whose summary could be read. Runs still being summarized are
  // excluded, so when that happens the panel has to admit it rather than presenting a partial week.
  const partialCoverage = !statsError && !!stats && stats.eligibleRuns > stats.totalRuns;

  const runs = runsData?.runs ?? [];
  const onFirstPage = table.page === 1;
  const latest = onFirstPage ? runs[0] : undefined;
  const rest = onFirstPage ? runs.slice(1) : runs;

  const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
  const recent = runs.filter((r) => new Date(r.startedAt).getTime() >= cutoff);

  const totalPages = runsData?.totalPages ?? 1;
  const open = (id: string) => navigate(`/vba-sync-cas/${encodeURIComponent(id)}`);

  return (
    <div className='min-h-screen bg-slate-50/70'>
      {/* Header */}
      <div className='bg-brand-navy'>
        <div className='max-w-6xl mx-auto px-8 py-8'>
          <div className='flex items-start justify-between gap-6 flex-wrap'>
            <div>
              <div className='flex items-center gap-3'>
                <div className='p-2 rounded-xl bg-white/10'>
                  <Activity className='h-5 w-5 text-white' />
                </div>
                <h1 className='text-2xl font-semibold text-white tracking-tight'>
                  Daily CAS Eligibility Sync
                </h1>
                <span className='px-2 py-0.5 rounded-full bg-brand-blue/25 text-brand-blue-light text-xs font-semibold tracking-wide'>
                  V4
                </span>
              </div>
              <p className='text-slate-300/80 text-sm mt-2 max-w-xl'>
                Every nightly run that wrote eligibility into VBA, with the reports it produced.
              </p>
            </div>

            <div className='flex items-center gap-2'>
              <Button
                variant='ghost'
                size='sm'
                className='text-white hover:bg-white/10 hover:text-white'
                onClick={() => navigate('/vba-sync-cas/mappings')}
              >
                <TableProperties className='h-4 w-4 mr-2' />
                Mappings & Notes
              </Button>
              <Button
                variant='ghost'
                size='sm'
                className='text-white hover:bg-white/10 hover:text-white'
                disabled={isFetching}
                onClick={() => void Promise.all([refetch(), refetchStats()])}
              >
                {isFetching ? (
                  <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                ) : (
                  <RefreshCw className='h-4 w-4 mr-2' />
                )}
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className='max-w-6xl mx-auto px-8 py-8 space-y-6'>
        {error ? (
          <Alert variant='destructive'>
            <AlertCircle className='h-4 w-4' />
            <AlertTitle>Could not load sync runs</AlertTitle>
            <AlertDescription className='space-y-3'>
              <p>{error.message}</p>
              <Button
                size='sm'
                variant='outline'
                disabled={isFetching}
                onClick={() => void Promise.all([refetch(), refetchStats()])}
              >
                {isFetching ? (
                  <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                ) : (
                  <RefreshCw className='h-4 w-4 mr-2' />
                )}
                Try again
              </Button>
            </AlertDescription>
          </Alert>
        ) : (
          <>
            {statsError && (
              <Alert variant='destructive'>
                <AlertCircle className='h-4 w-4' />
                <AlertTitle>Could not load the 7-day totals</AlertTitle>
                <AlertDescription>
                  The runs below are unaffected — only the summary failed. {statsError.message}
                </AlertDescription>
              </Alert>
            )}

            {/* Latest run */}
            {isLoading ? (
              <Skeleton className='h-32 w-full rounded-2xl' />
            ) : (
              latest && <LatestRun run={latest} onOpen={() => open(latest.id)} />
            )}

            {/* Seven-day summary + pulse */}
            <div className='rounded-2xl border border-slate-200 bg-white p-6'>
              <div className='flex items-baseline justify-between mb-5 gap-4'>
                <h2 className='text-sm font-semibold text-brand-navy'>Last 7 days</h2>
                {/*
                  When some runs in the window aren't summarized yet, say so rather than letting a
                  partial total pass for the whole week — an understated error count reads as a
                  quiet week, which is the one thing this panel must never get wrong.
                */}
                <span className='text-xs text-slate-400 text-right'>
                  {statsLoading
                    ? ''
                    : partialCoverage
                      ? `covers ${num(stats!.totalRuns)} of ${num(stats!.eligibleRuns)} runs — the rest are still being summarized`
                      : `${stat(() => num(stats?.totalRuns ?? 0))} runs`}
                </span>
              </div>

              <div className='grid grid-cols-2 md:grid-cols-4 gap-6 mb-6'>
                {statsLoading ? (
                  [0, 1, 2, 3].map((i) => <Skeleton key={i} className='h-12 w-full' />)
                ) : (
                  <>
                    <Figure label='Rows written' value={stat(() => num(stats?.totalRows ?? 0))} />
                    <Figure
                      label='Errors'
                      value={stat(() => num(stats?.totalErrors ?? 0))}
                      tone={(stats?.totalErrors ?? 0) > 0 ? 'error' : 'muted'}
                    />
                    <Figure
                      label='Warnings'
                      value={stat(() => num(stats?.totalWarnings ?? 0))}
                      tone={(stats?.totalWarnings ?? 0) > 0 ? 'warning' : 'muted'}
                    />
                    <Figure
                      label='Success rate'
                      value={stat(() => formatRate(stats?.successRate))}
                    />
                  </>
                )}
              </div>

              {!isLoading && recent.length > 0 && (
                <div className='space-y-2'>
                  <div className='text-[11px] uppercase tracking-wider text-slate-400'>
                    Each run in the last 7 days
                  </div>
                  <RunStrip runs={recent} onSelect={open} />
                </div>
              )}
            </div>

            {/* Run feed */}
            <div className='rounded-2xl border border-slate-200 bg-white overflow-hidden'>
              <div className='px-5 py-3.5 border-b border-slate-100 flex items-center justify-between'>
                <h2 className='text-sm font-semibold text-brand-navy'>
                  {onFirstPage ? 'Earlier runs' : 'Runs'}
                </h2>
                <span className='text-xs text-slate-400 tabular-nums'>
                  {runsData ? `${num(runsData.totalCount)} total` : ''}
                </span>
              </div>

              <div className='divide-y divide-slate-100'>
                {isLoading ? (
                  [0, 1, 2, 3, 4].map((i) => (
                    <div key={i} className='px-5 py-4'>
                      <Skeleton className='h-5 w-full' />
                    </div>
                  ))
                ) : rest.length === 0 ? (
                  <div className='px-5 py-12 text-center text-sm text-slate-400'>
                    No sync runs found
                  </div>
                ) : (
                  rest.map((run) => <RunRow key={run.id} run={run} onOpen={() => open(run.id)} />)
                )}
              </div>

              {totalPages > 1 && (
                <div className='px-5 py-3 border-t border-slate-100 flex items-center justify-between'>
                  <span className='text-xs text-slate-400 tabular-nums'>
                    Page {table.page} of {totalPages}
                  </span>
                  <div className='flex items-center gap-2'>
                    <Button
                      size='sm'
                      variant='outline'
                      disabled={table.page <= 1 || isFetching}
                      onClick={() => table.setPage(table.page - 1)}
                    >
                      <ChevronLeft className='h-4 w-4 mr-1' />
                      Previous
                    </Button>
                    <Button
                      size='sm'
                      variant='outline'
                      disabled={table.page >= totalPages || isFetching}
                      onClick={() => table.setPage(table.page + 1)}
                    >
                      Next
                      <ChevronRight className='h-4 w-4 ml-1' />
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Legend — stated once, rather than repeated on every row */}
            <div className='flex items-center gap-5 text-xs text-slate-400 px-1 flex-wrap'>
              <span className='flex items-center gap-1.5'>
                <span className='h-2 w-2 rounded-full bg-emerald' />
                Completed
              </span>
              <span className='flex items-center gap-1.5'>
                <span className='h-2 w-2 rounded-full bg-red-500' />
                Failed
              </span>
              <span className='text-slate-400'>
                Errors are rows the sync held back for review — a completed run can still have them.
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
